<template>
  <div class="grid lg:grid-cols-3 gap-4 lg:gap-6 auto-rows-auto">
    <div class="space-y-4 lg:space-y-6">
      <Card v-if="titleEntity.attributes.gating">
        <h3 class="font-medium">Instant Access</h3>
        <p v-if="numberCondition">
          {{
            $t(
              `site.title.id.slug.details.gating.${titleEntity.attributes.gating}.${numberCondition.attributes.strategy}`,
              { count: numberCount },
            )
          }}
        </p>
        <p v-else>
          {{
            $t(
              `site.title.id.slug.details.gating.${titleEntity.attributes.gating}.all.${exceptions ? "exceptions" : "default"}`,
            )
          }}
        </p>
        <p v-if="exceptions && numberCondition">
          {{
            $t(
              `site.title.id.slug.details.gating.${titleEntity.attributes.gating}.exceptions`,
              exceptionCount,
            )
          }}
        </p>
        <p v-if="tierCondition" class="mb-2">
          {{ $t(`site.title.id.slug.details.gating.unlock_by_tier`) }}
        </p>
        <TheNuxtLink
          v-if="requiredTier && titleOrg"
          :href="href.org.slug.subscriptions(titleOrg.attributes!.slug)"
        >
          <SubscriptionListCard class="mb-4" :tier="requiredTier" no-details />
        </TheNuxtLink>
      </Card>
      <Card>
        <h3 class="font-medium mb-4">Publication information</h3>
        <table class="table-fixed w-full">
          <tbody class="divide-y divide-neutral-300 dark:divide-neutral-600">
            <tr>
              <td>Status</td>
              <td>
                {{
                  translate(
                    `entity.title.publication_status.${titleEntity.attributes.publicationStatus}`,
                  )
                }}
              </td>
            </tr>
            <tr>
              <td>Year</td>
              <td>{{ titleEntity.attributes.year }}</td>
            </tr>
            <tr>
              <td>Original Language</td>
              <td>
                {{
                  translate(
                    `languages.${titleEntity.attributes.originalLanguage}`,
                  )
                }}
              </td>
            </tr>
            <tr>
              <td>Content Rating</td>
              <td>
                {{
                  translate(
                    `entity.title.content_rating.${titleEntity.attributes.contentRating}`,
                  )
                }}
              </td>
            </tr>
            <tr>
              <td>Format</td>
              <td>
                {{
                  translate(
                    `entity.title.format.${titleEntity.attributes.type}`,
                  )
                }}
              </td>
            </tr>
            <tr>
              <td>Reading Direction</td>
              <td>
                {{
                  translate(
                    `entity.title.reading_mode.${titleEntity.attributes.readingMode}`,
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
    <div class="lg:col-span-2 space-y-4 lg:space-y-6">
      <Card class="space-y-2">
        <template
          v-if="
            getTagGroup('format').length || getTagGroup('admin-only').length
          "
        >
          <h3 class="font-medium">Categories</h3>
          <TagGroup :tags="getTagGroup('format')">
            <Tag
              v-for="tag in getTagGroup('admin-only')"
              :type="getAwardColor(tag.id)"
              small
              :tag="tag.id"
            >
              {{ $t(`components.tag.${tag.id}.name`) }}
            </Tag>
          </TagGroup>
        </template>

        <template v-if="getTagGroup('content-warnings').length">
          <h3 class="font-medium">Content</h3>
          <TagGroup :tags="getTagGroup('content-warnings')" />
        </template>

        <h3 class="font-medium">Genres</h3>
        <TagGroup :tags="getTagGroup('genre')">
          <Tag
            v-if="primaryGenre && primaryGenre.id"
            type="primary"
            :tag="primaryGenre.id"
          >
            {{ $t(`components.tag.${primaryGenre.id}.name`) }}
          </Tag>
          <Tag
            v-if="secondaryGenre && secondaryGenre.id"
            type="secondary"
            :tag="secondaryGenre.id"
          >
            {{ $t(`components.tag.${secondaryGenre.id}.name`) }}
          </Tag>
        </TagGroup>

        <template v-if="getTagGroup('theme').length">
          <h3 class="font-medium">Themes</h3>
          <TagGroup :tags="getTagGroup('theme')" />
        </template>
      </Card>
      <Card class="space-y-8" v-if="!titleRelations || titleRelations.length">
        <NamiLoading v-if="pending" />
        <template v-else>
          <div v-for="section in titleRelations">
            <h3 class="font-medium mb-4">{{ section.relation }}</h3>
            <TitlesView
              :titles="section.titles"
              :meta="{ limit: 0, offset: 0, total: 0 }"
            />
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Gating,
  isOrg,
  type TagGroup,
  Tier,
  Title,
  type TitleEntity,
} from "~/src/api";
import {
  getSupportedRelationTypes,
  getTitleRelations,
  type TitleRelationType,
} from "~/src/temp/titleRelations";

const dayjs = useDayjs();
const nuxtApp = useNuxtApp();
const { t: translate } = useI18n();
const href = useHref();
const props = defineProps<{
  titleEntity: TitleEntity;
}>();

const titleOrg = computed(() => props.titleEntity.relationships.find(isOrg));

const { primaryGenre, secondaryGenre, demographic, tags } =
  parseTitleForDisplay(computed(() => props.titleEntity));

const getTagGroup = (group: TagGroup) =>
  tags.value.filter((x) => x.attributes?.group === group);
const getAwardColor = (tag: string) => {
  if (tag === "namis-tt-gold-winner") return "gold";
  if (tag === "namis-tt-silver-winner") return "silver";
  return "primary";
};

const { data } = useAsyncData(async () => {
  return await Gating.getConditions("title", props.titleEntity.id, {
    includes: ["tier_item"],
  });
});

const { data: orgTiers } = useAsyncData(async () => {
  if (!titleOrg.value) return;
  return await Tier.search({
    organizationId: titleOrg.value.id,
    order: { amount: "asc" },
    currency: "USD",
    showDeleted: false,
  });
});

const relations = getSupportedRelationTypes();

const { data: titleRelations, pending } = useAsyncData(
  `title-${props.titleEntity.id}-relations`,
  async () => {
    const relationMap = getTitleRelations(props.titleEntity);

    const allTitleIds = Array.from(new Set(Object.values(relationMap).flat()));
    if (!allTitleIds.length) return [];

    const titles = await Title.search({
      ids: allTitleIds,
      includes: ["cover_art"],
    });

    const titleMap = Object.fromEntries(titles.data.map((t) => [t.id, t]));

    const entries = Object.entries(relationMap);
    const mapped = entries
      .filter(([_, t]) => t.length)
      .map(([entry, ids]) => ({
        relation: entry as TitleRelationType,
        titles: ids.map((id) => titleMap[id]).filter(onlyTruthys),
      }));

    return mapped.sort(
      (a, b) => relations.indexOf(a.relation) - relations.indexOf(b.relation),
    );
  },
);

const numberConditions = ["first_n", "last_n", "published_age"];
const tierConditions = [
  "subscribed_tier_amount",
  "subscribed_tier_ids",
  "subscribed_to_organization",
  "follows_organization",
];
const numberCondition = computed(() => {
  return data.value?.data.find((condition) =>
    numberConditions.includes(condition.attributes.strategy),
  );
});
const numberCount = computed(() => {
  if (!numberCondition.value) return 0;
  const count = (
    numberCondition.value.attributes.parameters as { count: number }
  ).count;
  const days =
    ((numberCondition.value.attributes.parameters as { age: number }).age ??
      0) /
    60 /
    60 /
    24;
  return count || days || 0;
});

const tierCondition = computed(() => {
  return data.value?.data.find((condition) =>
    tierConditions.includes(condition.attributes.strategy),
  );
});
const requiredTier = computed(() => {
  if (tierCondition.value?.attributes.strategy === "subscribed_tier_amount") {
    const minAmount = (
      tierCondition.value?.attributes.parameters as { amount: number }
    )?.amount;

    return orgTiers.value?.data.find(
      (tier) => tier.attributes.minAmounts.USD! >= minAmount,
    );
  }

  if (tierCondition.value?.attributes.strategy === "subscribed_tier_ids") {
    const tiers = (
      tierCondition.value?.attributes.parameters as { tierItemIds: string[] }
    )?.tierItemIds;

    return orgTiers.value?.data.find((tier) => tiers.includes(tier.id));
  }

  if (
    tierCondition.value?.attributes.strategy === "subscribed_to_organization"
  ) {
    return orgTiers.value?.data.at(0);
  }

  return undefined;
});

const exceptions = computed(() => {
  return data.value?.data.find(
    (condition) => condition.attributes.strategy === "chapter_ids",
  );
});
const exceptionCount = computed(() => {
  return (exceptions.value?.attributes.parameters as { chapterIds: string[] })
    ?.chapterIds.length;
});

// TODO: add rating information
</script>

<style scoped lang="postcss">
tr td {
  @apply py-1;
}

tr td:first-child {
  @apply text-left;
}

tr td:last-child {
  @apply text-right;
}
</style>
